import { useEffect, useMemo, useRef, useState } from 'react'
import type { PrioridadeTriagem } from '../../dominio/didatico/ocupantes'
import { FASES_ROTEIRO, type EstadoPublicoTabletop } from '../../dominio/tabletop/atendimento'
import { VisualizadorProjecaoTabletop } from '../../visualizacao3d/tabletop/VisualizadorProjecaoTabletop'
import {
  LIMITE_DESCONEXAO_MS,
  canalProjecao,
  type RelogioPublicoTabletop,
} from './canalProjecaoTabletop'

const CORES_TRIAGEM: Record<PrioridadeTriagem, string> = {
  vermelho: '#ef4444',
  amarelo: '#eab308',
  verde: '#22c55e',
  preto: '#94a3b8',
}

/** Enquanto nada chegou, a projeção repete o pedido de estado neste intervalo. */
const INTERVALO_NOVO_PEDIDO_MS = 1500

function formatarTempo(segundos: number): string {
  const min = Math.floor(segundos / 60)
  const seg = segundos % 60
  return `${min.toString().padStart(2, '0')}:${seg.toString().padStart(2, '0')}`
}

interface ProjecaoRecebida {
  readonly estado: EstadoPublicoTabletop | null
  readonly relogio: RelogioPublicoTabletop | null
  readonly revisao: number
  readonly conectada: boolean
}

/**
 * Lado da projeção no canal: pede o estado ao abrir (sem esperar o instrutor
 * clicar em nada), guarda só a revisão mais nova da sessão atual e marca
 * desconexão quando o sinal de vida da janela do instrutor para de chegar.
 */
function useProjecaoRecebida(): ProjecaoRecebida {
  const [estado, setEstado] = useState<EstadoPublicoTabletop | null>(null)
  const [relogio, setRelogio] = useState<RelogioPublicoTabletop | null>(null)
  const [revisao, setRevisao] = useState(0)
  const [ultimoContato, setUltimoContato] = useState<number | null>(null)
  const [agora, setAgora] = useState(() => Date.now())
  const ultimaRevisaoRef = useRef<{ idSessao: string; revisao: number } | null>(null)

  useEffect(() => {
    const pararDeOuvir = canalProjecao.ouvir((msg) => {
      if (msg.tipo === 'SOLICITAR_ESTADO') return
      setUltimoContato(Date.now())
      if (msg.tipo === 'RELOGIO') {
        setRelogio(msg.relogio)
        return
      }
      // dentro da mesma sessão, descarta revisão atrasada; sessão nova
      // (instrutor reiniciou ou reabriu a janela) é aceita do zero
      const ultima = ultimaRevisaoRef.current
      if (ultima && ultima.idSessao === msg.estado.idSessao && msg.revisao < ultima.revisao) return
      ultimaRevisaoRef.current = { idSessao: msg.estado.idSessao, revisao: msg.revisao }
      setEstado(msg.estado)
      setRevisao(msg.revisao)
      setRelogio(msg.relogio)
    })
    canalProjecao.enviar({ tipo: 'SOLICITAR_ESTADO' })
    return pararDeOuvir
  }, [])

  // sem estado ainda (instrutor abriu depois, ou recarregou): pede de novo
  useEffect(() => {
    if (estado) return
    const intervalo = setInterval(() => canalProjecao.enviar({ tipo: 'SOLICITAR_ESTADO' }), INTERVALO_NOVO_PEDIDO_MS)
    return () => clearInterval(intervalo)
  }, [estado])

  useEffect(() => {
    const intervalo = setInterval(() => setAgora(Date.now()), 1000)
    return () => clearInterval(intervalo)
  }, [])

  const conectada = ultimoContato !== null && agora - ultimoContato < LIMITE_DESCONEXAO_MS
  return { estado, relogio, revisao, conectada }
}

/**
 * Tela limpa para o projetor/2ª tela: a cena 3D da ocorrência e um cartão por
 * vítima JÁ IDENTIFICADA com o que foi liberado. Tudo vem do estado público
 * (`montarEstadoPublico`) — esta tela não conhece fichas, gabarito nem
 * anotações, e não consulta nada além do que recebeu pelo canal.
 */
export function TelaProjecaoTabletop() {
  const { estado, relogio, revisao, conectada } = useProjecaoRecebida()

  const triagensDeclaradas = useMemo(() => {
    const mapa: Record<string, PrioridadeTriagem> = {}
    for (const v of estado?.vitimas ?? []) if (v.triagemDeclarada) mapa[v.id] = v.triagemDeclarada
    return mapa
  }, [estado])
  const colaresColocados = useMemo(() => {
    const mapa: Record<string, boolean> = {}
    for (const v of estado?.vitimas ?? []) if (v.colarColocado) mapa[v.id] = true
    return mapa
  }, [estado])

  if (!estado) {
    return (
      <div className="atd-projecao">
        <p>Aguardando a janela do instrutor… Mantenha o Table top aberto na tela principal.</p>
      </div>
    )
  }

  const pergunta = FASES_ROTEIRO[estado.fase]

  return (
    <div className="atd-projecao atd-projecao--cena">
      <div className="atd-linha-controles">
        <div>
          <span className="atd-pergunta__rotulo">{pergunta.rotulo}</span>
          <p className="atd-projecao__pergunta" style={{ margin: 0 }}>{pergunta.pergunta}</p>
        </div>
        <div className="atd-projecao__status">
          <span
            className={`atd-projecao__conexao ${conectada ? '' : 'atd-projecao__conexao--perdida'}`}
            title={`Revisão ${revisao}`}
          >
            {conectada ? 'Conectado ao instrutor' : 'Sem sinal da janela do instrutor'}
          </span>
          <span className="atd-cronometro">{formatarTempo(relogio?.tempoSimuladoSegundos ?? 0)}</span>
        </div>
      </div>

      <div className="atd-projecao__palco">
        <VisualizadorProjecaoTabletop
          objetos={estado.objetos}
          triagensDeclaradas={triagensDeclaradas}
          colaresColocados={colaresColocados}
          sangramentos={estado.sangramentos}
          riscos={estado.riscos}
        />
      </div>

      {estado.vitimas.length > 0 && (
        <div className="atd-projecao__grade">
          {estado.vitimas.map((v) => (
            <div key={v.id} className="atd-projecao__cartao">
              <div className="atd-linha-controles">
                <strong style={{ fontSize: 'var(--text-lg)' }}>{v.nome}</strong>
                <span
                  className="atd-chip-vitima__cor"
                  style={{
                    background: v.triagemDeclarada ? CORES_TRIAGEM[v.triagemDeclarada] : 'var(--color-text-secondary)',
                    width: 16,
                    height: 16,
                  }}
                />
              </div>
              {v.papel && <span className="atd-resumo-ficha__mecanismo">{v.papel}</span>}

              <div className="atd-monitor" style={{ marginTop: 'var(--space-3)' }}>
                {v.leituras.map((leitura) => (
                  <div key={leitura.sigla} className="atd-monitor__slot" title={leitura.rotulo}>
                    <span className="atd-monitor__sigla">{leitura.sigla}</span>
                    <span className={`atd-monitor__valor ${leitura.valor ? '' : 'atd-monitor__valor--vazio'}`}>
                      {leitura.valor ?? '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
